/**
 * Shared difficulty utilities.
 *
 * All difficulty display logic goes here so components don't duplicate
 * color maps, label formatting, etc.
 */

import type { DifficultyInfo } from '../components/admin/problems/types.js'

/**
 * Fallback color classes when DifficultyLevel.color is not available.
 * Maps difficulty name (case-insensitive) to Tailwind classes.
 */
const DIFFICULTY_COLOR_CLASSES: Record<string, string> = {
  easy: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  medium:
    'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  hard: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
}

const DEFAULT_COLOR_CLASS =
  'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400'

/**
 * Get Tailwind color classes for a difficulty level.
 * Falls back to name-based lookup if hex color not provided.
 */
export function getDifficultyColorClass(
  difficulty: DifficultyInfo | null | undefined,
): string {
  if (!difficulty?.name) return DEFAULT_COLOR_CLASS
  return (
    DIFFICULTY_COLOR_CLASSES[difficulty.name.toLowerCase()] ||
    DEFAULT_COLOR_CLASS
  )
}

/**
 * Get display label for a difficulty (title-cased name).
 */
export function getDifficultyLabel(
  difficulty: DifficultyInfo | null | undefined,
): string {
  return difficulty?.name ?? 'Not set'
}

/**
 * Get the inline style for difficulty hex color (for badges with custom colors).
 */
export function getDifficultyStyle(
  difficulty: DifficultyInfo | null | undefined,
): React.CSSProperties | undefined {
  if (!difficulty?.color) return undefined
  return { color: difficulty.color }
}
